import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as authApi from '../api/auth'
import * as accountApi from '../api/account'
import { jestPolska } from '../kraj'
import { ustawKontoFirmowe } from '../lib/aktywnosc'
import { odczytajPodglad, profilZPodgladem } from '../lib/plany'

// Stan zalogowania dla całej aplikacji.
//
// Źródłem prawdy jest SERWER, nie localStorage. Przy sesji w ciasteczku HttpOnly
// front nie ma jak samodzielnie stwierdzić, czy sesja jeszcze żyje — musi zapytać.
// Trzymanie „jestem zalogowany" w localStorage prowadziłoby do interfejsu, który
// pokazuje zalogowanego użytkownika po wygaśnięciu sesji.

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  // `loading` na starcie jest true: dopóki nie wiemy, kto jest zalogowany, nie
  // wolno renderować ani „Zaloguj się", ani panelu użytkownika — inaczej przy
  // każdym wejściu mignąłby zły stan.
  const [loading, setLoading] = useState(true)
  // Profil konta (rodzaj konta, firma, typy działalności) — POZA sesją allauth,
  // która wie tylko, kto jest zalogowany. Trzymany tutaj, a nie doczytywany
  // w każdym komponencie, bo pytają o niego miejsca rozsiane po całym interfejsie
  // (menu konta, wyceny), a to jedna i ta sama odpowiedź na całą sesję.
  const [profil, setProfil] = useState(null)
  // Profil nie doszedł (błąd sieci, 5xx). Bez tej flagi „profil się wczytuje” trwałoby w nieskończoność,
  // a bramki planu (lib/plany) czekałyby z treścią na zawsze.
  const [profilNiedostepny, setProfilNiedostepny] = useState(false)

  const applyResponse = useCallback((res) => {
    const authenticated = Boolean(res.meta && res.meta.is_authenticated)
    setUser(authenticated ? (res.data && res.data.user) || null : null)
    return res
  }, [])

  // Jedno wywołanie przy starcie: ustawia ciasteczko CSRF i mówi, kto jest zalogowany.
  // Poza Polską kont nie ma (logowanie i maile są tylko po polsku), więc nie pytamy o sesję
  // i nie zostawiamy w przeglądarce ciasteczka, które niczemu by nie służyło (2026-10-09).
  useEffect(() => {
    if (!jestPolska) { setLoading(false); return undefined }
    let cancelled = false
    authApi.fetchSession()
      .then((res) => { if (!cancelled) applyResponse(res) })
      .catch(() => { if (!cancelled) setUser(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [applyResponse])

  // Profil doczytujemy PO ustaleniu sesji. Osobne żądanie, bo endpoint allauth
  // nie zna naszych pól — a bez `account_type` menu nie wie, czy pokazać
  // pozycje firmowe.
  useEffect(() => {
    setProfilNiedostepny(false)
    if (!user) { setProfil(null); return undefined }
    let anulowane = false
    accountApi.getProfile().then((res) => {
      if (anulowane) return
      if (res.ok) setProfil(res.data)
      else setProfilNiedostepny(true)
    })
    return () => { anulowane = true }
  }, [user])

  // Podgląd planów na localhost (`?plan=free` itd., lib/plany): podmienia pola planu w profilu dla całej aplikacji,
  // żeby limit kont, kłódki i licznik próby mówiły to samo. Poza adresem lokalnym zawsze `null`.
  const { search } = useLocation()
  const podgladPlanu = useMemo(() => odczytajPodglad(), [search]) // eslint-disable-line react-hooks/exhaustive-deps
  const profilWidoczny = useMemo(() => profilZPodgladem(profil, podgladPlanu), [profil, podgladPlanu])

  // Dziennik aktywności firm (`lib/aktywnosc`) wysyła zdarzenia z przeglądarki tylko dla zalogowanego konta firmowego.
  useEffect(() => { ustawKontoFirmowe(Boolean(user) && profil?.account_type === 'company') }, [user, profil])

  // Ponowne pobranie profilu po zmianie, którą widać w interfejsie poza kontem (np. logo firmy w pasku).
  const odswiezProfil = useCallback(async () => {
    const res = await accountApi.getProfile()
    if (res.ok) setProfil(res.data)
  }, [])

  const login = useCallback(async (credentials) => {
    const res = await authApi.login(credentials)
    applyResponse(res)
    return res
  }, [applyResponse])

  const logout = useCallback(async () => {
    const res = await authApi.logout()
    // Wylogowanie czyścimy lokalnie niezależnie od odpowiedzi: jeśli sesja już
    // wygasła po stronie serwera, żądanie zwróci błąd, a użytkownik i tak ma
    // zostać wylogowany w interfejsie.
    setUser(null)
    return res
  }, [])

  const refresh = useCallback(async () => applyResponse(await authApi.fetchSession()), [applyResponse])

  // Uprawnienia w zespole firmy (konta zespołowe, 2026-10-11). Konto bez firmy może wszystko na swoich
  // danych, właściciel ma komplet z serwera. Służy tylko do chowania przycisków: pilnuje tego API.
  const moze = useCallback(
    (klucz) => !profilWidoczny?.company || (profilWidoczny.company.uprawnienia || []).includes(klucz),
    [profilWidoczny],
  )

  const value = useMemo(() => ({
    user,
    profil: profilWidoczny,
    // Skrót używany w kilku miejscach; `false` dopóki profil się nie doczyta,
    // żeby pozycje firmowe nie mignęły na koncie prywatnym.
    firmowe: profilWidoczny?.account_type === 'company',
    loading,
    isAuthenticated: Boolean(user),
    // Zalogowany, a profil (z planem firmy) jeszcze w drodze. Bramki planu czekają wtedy jak przy `loading`.
    profilUstalany: Boolean(user) && !profil && !profilNiedostepny,
    podgladPlanu,
    login,
    logout,
    refresh,
    odswiezProfil,
    moze,
  }), [user, profil, profilWidoczny, profilNiedostepny, podgladPlanu, loading, login, logout, refresh, odswiezProfil, moze])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth musi być użyte wewnątrz <AuthProvider>')
  return ctx
}
